/* finger-chooser/sound.js — synthesised cues (no sample files) and haptics.
 *
 * The context goes through the site primitive `__liveAudioCtx(Ready)`: it
 * owns the «output device was taken away» case, where a plain resume() hangs
 * forever and every later cue is silence without an error.
 *
 * The autoplay rules decide WHEN sound can start, and a finger game hits the
 * awkward one: `touchstart` is NOT a user activation (only touchend, click,
 * keydown are). So the first round on a fresh page — everyone touching,
 * nobody having lifted a finger yet — may be silent and without vibration on
 * a browser that has never seen a tap on this page. `prime()` is called from
 * every activation-granting event (touchend, click) so from the first lifted
 * finger on, the cues work. Nothing is created before an activation: an
 * AudioContext made without one only earns a console warning.
 */

let ctxRef = null;
let enabled = true;
let hapticsOn = true;

function AC() {
    return (typeof window !== 'undefined') && (window.AudioContext || window.webkitAudioContext) || null;
}
function makeCtx() {
    const C = AC();
    return C ? new C({ latencyHint: 'interactive' }) : null;
}
function activated() {
    try {
        const ua = navigator.userActivation;
        return !ua || ua.hasBeenActive !== false;
    } catch (_) { return true; }
}

export function setEnabled(v) { enabled = !!v; }
export function setHaptics(v) { hapticsOn = !!v; }
export function hapticsSupported() {
    try { return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function'; }
    catch (_) { return false; }
}

/* Call from a user-activation event handler (touchend / click). */
export function prime() {
    if (!enabled || !AC() || !activated()) return;
    try {
        if (typeof window.__liveAudioCtx === 'function') ctxRef = window.__liveAudioCtx(ctxRef, makeCtx);
        else if (!ctxRef) ctxRef = makeCtx();
        if (ctxRef && ctxRef.state === 'suspended' && ctxRef.resume) ctxRef.resume().catch(() => {});
    } catch (_) { /* the ready() path reports */ }
}

async function ready() {
    if (!enabled || !AC() || !activated()) return null;
    try {
        if (typeof window.__liveAudioCtxReady === 'function') {
            ctxRef = await window.__liveAudioCtxReady(ctxRef, makeCtx);
        } else {
            if (!ctxRef) ctxRef = makeCtx();
            if (ctxRef && ctxRef.state === 'suspended') await ctxRef.resume();
        }
    } catch (_) { return null; }
    return ctxRef && ctxRef.state === 'running' ? ctxRef : null;
}

function tone(c, f0, dur, o) {
    o = o || {};
    const t0 = c.currentTime + (o.delay || 0);
    const osc = c.createOscillator();
    const g = c.createGain();
    osc.type = o.type || 'sine';
    osc.frequency.setValueAtTime(f0, t0);
    if (o.f1) osc.frequency.exponentialRampToValueAtTime(o.f1, t0 + dur);
    const peak = o.gain || 0.18;
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(peak, t0 + Math.min(0.012, dur * 0.3));
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    osc.connect(g);
    g.connect(c.destination);
    osc.start(t0);
    osc.stop(t0 + dur + 0.03);
}

const CUES = {
    finger(c, n) { tone(c, 480 + Math.min(10, n || 1) * 45, 0.07, { gain: 0.1 }); },
    tick(c) { tone(c, 1500, 0.025, { type: 'square', gain: 0.045 }); },
    reveal(c, place) { tone(c, 520 * Math.pow(1.12, Math.min(12, (place || 1) - 1)), 0.09, { type: 'triangle', gain: 0.16 }); },
    winner(c) {
        [523.25, 659.25, 783.99, 1046.5].forEach((f, i) => tone(c, f, 0.32, { type: 'triangle', gain: 0.2, delay: i * 0.085 }));
    },
    loser(c) {
        tone(c, 392, 0.34, { type: 'sawtooth', gain: 0.07, f1: 370 });
        tone(c, 311.13, 0.62, { type: 'sawtooth', gain: 0.07, f1: 262, delay: 0.36 });
        tone(c, 196, 0.62, { gain: 0.12, f1: 131, delay: 0.36 });
    },
    teams(c) {
        tone(c, 440, 0.4, { type: 'triangle', gain: 0.14 });
        tone(c, 554.37, 0.4, { type: 'triangle', gain: 0.12 });
        tone(c, 587.33, 0.5, { type: 'triangle', gain: 0.14, delay: 0.18 });
        tone(c, 739.99, 0.5, { type: 'triangle', gain: 0.12, delay: 0.18 });
    },
    cancel(c) { tone(c, 300, 0.18, { type: 'square', gain: 0.06, f1: 200 }); },
};

export function play(kind, arg) {
    if (!enabled) return;
    const cue = CUES[kind];
    if (!cue) return;
    // Synchronous fast path: most cues fire while the context already runs.
    if (ctxRef && ctxRef.state === 'running') {
        try { cue(ctxRef, arg); } catch (_) {}
        return;
    }
    ready().then((c) => { if (c) { try { cue(c, arg); } catch (_) {} } });
}

const PATTERNS = {
    finger: 12,
    result: [70, 50, 150],
    loser: [220],
    cancel: [40, 40, 40],
};

export function buzz(kind) {
    if (!hapticsOn || !hapticsSupported() || !activated()) return;
    const p = PATTERNS[kind];
    if (p == null) return;
    try { navigator.vibrate(p); } catch (_) {}
}
